import { SKUS } from "@/domains/surveys/models/constants";

export type MockCatalogProduct = {
  productId: number;
  variantId: string;
  shopifyProductId: string;
  sku: string;
  name: string;
  shortName: string;
  handle: string;
  priceInCents: number;
  images: readonly string[];
  /*
    The simpler cutout the plan's product list shows (public/assets/products/cutouts, 400px,
    from Charlotte's cutout set). Left out where no cutout exists yet: the list then falls back
    to the first of `images`, as it will in production until the Shopify metafield is filled.
  */
  summaryImage?: string;
  /* Review aggregate, as Okendo reports it; null for products without reviews. */
  rating: number | null;
  reviewCount: number | null;
};

/*
  Product photography is the real EO set (public/assets/products, 1200px, from Charlotte's
  EO_Product_Images/web-1200 with its manifest), matched to each sample product by format:
  tincture, inhaler, gummies. Names and prices stay the sample ones.
*/
export const MOCK_CATALOG = {
  DAILY_BALANCE: {
    productId: 101,
    variantId: "910000000101",
    shopifyProductId: "920000000101",
    sku: "SAMPLE-TINCTURE-01",
    name: "Daily Balance Tincture",
    shortName: "Daily Balance",
    handle: "daily-balance-tincture",
    priceInCents: 4000,
    images: ["/assets/products/comfort.png", "/mocks/products/supplement-facts.svg"],
    summaryImage: "/assets/products/cutouts/comfort.png",
    rating: 4.6,
    reviewCount: 57,
  },
  QUICK_EASE: {
    productId: 102,
    variantId: "910000000102",
    shopifyProductId: "920000000102",
    sku: "SAMPLE-INHALER-02",
    name: "Quick Ease Inhaler",
    shortName: "Quick Ease",
    handle: "quick-ease-inhaler",
    priceInCents: 5000,
    images: ["/assets/products/rapid-relief.png"],
    summaryImage: "/assets/products/cutouts/rapid-relief.png",
    rating: 4.5,
    reviewCount: 318,
  },
  NIGHT_REST: {
    productId: 103,
    variantId: "910000000103",
    shopifyProductId: "920000000103",
    sku: "SAMPLE-GUMMIES-03",
    name: "Night Rest Gummies",
    shortName: "Night Rest",
    handle: "night-rest-gummies",
    priceInCents: 4000,
    images: ["/assets/products/bedtime.png"],
    summaryImage: "/assets/products/cutouts/bedtime.png",
    rating: 4.3,
    reviewCount: 44,
  },
  MORNING_FOCUS: {
    productId: 104,
    variantId: "910000000104",
    shopifyProductId: "920000000104",
    sku: "SAMPLE-CAPSULES-04",
    name: "Morning Focus Capsules",
    shortName: "Morning Focus",
    handle: "morning-focus-capsules",
    priceInCents: 4200,
    images: ["/assets/products/relief-max.png"],
    rating: null,
    reviewCount: null,
  },
  RAPID_RELIEF: {
    productId: 105,
    variantId: "910000000105",
    shopifyProductId: "920000000105",
    sku: SKUS.GUIDED_RAPID_RELIEF,
    name: "Rapid Relief Inhaler (Sample)",
    shortName: "Rapid Relief",
    handle: "rapid-relief-inhaler",
    priceInCents: 4900,
    images: ["/assets/products/rapid-relief.png"],
    summaryImage: "/assets/products/cutouts/rapid-relief.png",
    rating: null,
    reviewCount: null,
  },
  CALM_DAY: {
    productId: 106,
    variantId: "910000000106",
    shopifyProductId: "920000000106",
    sku: "SAMPLE-GUMMIES-06",
    name: "Calm Day Gummies",
    shortName: "Calm Day",
    handle: "calm-day-gummies",
    priceInCents: 3500,
    images: ["/assets/products/calm-gummy.png"],
    /* The red Relief+ gummy cutout stands in: it is the gummy cutout the set has. */
    summaryImage: "/assets/products/cutouts/relief-plus.png",
    rating: null,
    reviewCount: null,
  },
} as const satisfies Record<string, MockCatalogProduct>;

const catalogProducts: MockCatalogProduct[] = Object.values(MOCK_CATALOG);

export const findCatalogProductByVariantId = (variantId: string) => {
  return catalogProducts.find((product) => {
    return product.variantId === variantId;
  });
};

export const toDollars = (priceInCents: number) => {
  return priceInCents / 100;
};
